import React, { useMemo } from 'react';
import DOMPurify from 'dompurify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLightbulb } from '@fortawesome/free-solid-svg-icons';

/**
 * InsightPanel
 *
 * Renders an admin-authored "NBME Insight" for a single answer option.
 *
 * The insight arrives from the API as a raw HTML string (camelCase `insights`
 * on the answer object). We sanitize it with DOMPurify before injecting it via
 * dangerouslySetInnerHTML, then style the inner markup (headings / lists / bold
 * / paragraphs / etc.) for both light and dark mode through a scoped stylesheet.
 *
 * Visually this is intentionally distinct from the plain green/red explanation
 * block: a warm amber "study aid" card with a lightbulb header and an accent
 * rail, so students immediately recognize it as supplemental board-style insight.
 *
 * Props:
 *   - insightsHtml: HTML string (admin-authored)
 *   - isDarkMode:   boolean
 *   - themeStyles:  shared theme object (kept for API parity with siblings)
 */
const InsightPanel = ({ insightsHtml, isDarkMode, themeStyles }) => {
  // Sanitize once per html change. Admin-authored content is generally trusted,
  // but sanitizing is the safe default for injected HTML.
  const safeHtml = useMemo(() => {
    if (!insightsHtml || typeof insightsHtml !== 'string') return '';
    return DOMPurify.sanitize(insightsHtml, { USE_PROFILES: { html: true } });
  }, [insightsHtml]);

  if (!safeHtml) return null;

  // Amber/gold "scholarly" palette — deliberately different from the
  // green (correct) / red (incorrect) / blue (selected) language used elsewhere.
  //
  // Both modes use an OPAQUE surface so a transparent tint can never blend
  // toward the text color, and the body text is intentionally a high-contrast
  // neutral that can never equal the surface:
  //   - Light: deep warm-brown body on a soft amber-cream card.
  //   - Dark:  near-white body on an *elevated slate* surface (not brown/black,
  //            clearly distinct from the page), with a lighter amber accent so
  //            the headers pop. Mirrors how the sibling explanation blocks in
  //            CleanAnswerOptions derive their dark-mode colors.
  const accent = isDarkMode ? '#fcd34d' : '#b45309'; // amber-300 / amber-800
  const headerColor = isDarkMode ? '#fcd34d' : '#b45309'; // <h4> section headers
  const bodyColor = isDarkMode ? '#e8edf5' : '#3f3322'; // near-white / dark brown
  const strongColor = isDarkMode ? '#ffffff' : '#1f2937'; // ink — NOT the accent
  const mutedColor = isDarkMode ? '#c7cedb' : '#6b5a42'; // em / secondary text
  const linkColor = isDarkMode ? '#fcd34d' : '#9a4a05';
  const borderColor = isDarkMode
    ? 'rgba(252, 211, 77, 0.35)'
    : 'rgba(180, 83, 9, 0.25)';
  // Opaque gradients — no see-through tint that could match the body color.
  const background = isDarkMode
    ? 'linear-gradient(135deg, #1f2533 0%, #171c27 100%)'
    : 'linear-gradient(135deg, #fffaf0 0%, #fef3d7 100%)';
  const chipBg = isDarkMode ? 'rgba(252, 211, 77, 0.16)' : 'rgba(180, 83, 9, 0.10)';
  const codeBg = isDarkMode ? 'rgba(0, 0, 0, 0.30)' : 'rgba(180, 83, 9, 0.08)';
  const ruleColor = isDarkMode ? 'rgba(252, 211, 77, 0.22)' : 'rgba(180, 83, 9, 0.18)';

  // Unique class so the scoped <style> only touches this card's inner HTML.
  const scopeClass = 'nbme-insight-body';

  return (
    <div
      className="nbme-insight-panel"
      style={{
        marginTop: '10px',
        marginLeft: '8px',
        position: 'relative',
        borderRadius: '12px',
        border: `1px solid ${borderColor}`,
        background,
        boxShadow: isDarkMode
          ? '0 6px 18px rgba(0, 0, 0, 0.35)'
          : '0 6px 18px rgba(180, 83, 9, 0.08)',
        overflow: 'hidden'
      }}
    >
      {/* Accent rail */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: '4px',
          background: `linear-gradient(to bottom, ${accent}, transparent)`
        }}
      />

      <div style={{ padding: '14px 16px 16px 20px' }}>
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '9px',
            marginBottom: '10px'
          }}
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '26px',
              height: '26px',
              borderRadius: '8px',
              backgroundColor: chipBg,
              color: accent,
              flexShrink: 0
            }}
          >
            <FontAwesomeIcon icon={faLightbulb} style={{ fontSize: '14px' }} />
          </span>
          <span
            style={{
              fontSize: '12px',
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: headerColor
            }}
          >
            NBME Insight
          </span>
        </div>

        {/* Scoped styling for the injected admin HTML */}
        <style>{`
          .${scopeClass} {
            color: ${bodyColor};
            font-size: 15px;
            line-height: 1.62;
          }
          /* Catch-all: force every inline/text node to the readable body color so
             no injected element (incl. bare <span>/<div>) can inherit a color
             that matches the card surface. Specific rules below override this. */
          .${scopeClass} *,
          .${scopeClass} p,
          .${scopeClass} li,
          .${scopeClass} span,
          .${scopeClass} div { color: ${bodyColor}; }
          .${scopeClass} > *:first-child { margin-top: 0; }
          .${scopeClass} > *:last-child { margin-bottom: 0; }
          .${scopeClass} p { margin: 0 0 10px; }
          .${scopeClass} h1,
          .${scopeClass} h2,
          .${scopeClass} h3,
          .${scopeClass} h4,
          .${scopeClass} h5,
          .${scopeClass} h6 {
            color: ${headerColor};
            font-weight: 700;
            line-height: 1.3;
            margin: 14px 0 8px;
          }
          .${scopeClass} h1 { font-size: 18px; }
          .${scopeClass} h2 { font-size: 17px; }
          .${scopeClass} h3 { font-size: 16px; }
          .${scopeClass} h4,
          .${scopeClass} h5,
          .${scopeClass} h6 { font-size: 15px; }
          .${scopeClass} strong,
          .${scopeClass} b { color: ${strongColor}; font-weight: 700; }
          .${scopeClass} em,
          .${scopeClass} i { color: ${mutedColor}; font-style: italic; }
          .${scopeClass} ul,
          .${scopeClass} ol { margin: 6px 0 12px; padding-left: 22px; }
          .${scopeClass} li { margin: 4px 0; }
          .${scopeClass} ul li::marker { color: ${accent}; }
          .${scopeClass} ol li::marker { color: ${accent}; font-weight: 600; }
          .${scopeClass} a {
            color: ${linkColor};
            text-decoration: underline;
            text-underline-offset: 2px;
          }
          .${scopeClass} a:hover { opacity: 0.85; }
          .${scopeClass} blockquote {
            margin: 10px 0;
            padding: 6px 14px;
            border-left: 3px solid ${accent};
            background: ${chipBg};
            border-radius: 0 8px 8px 0;
          }
          .${scopeClass} code {
            font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
            font-size: 0.9em;
            background: ${codeBg};
            padding: 1px 6px;
            border-radius: 5px;
          }
          .${scopeClass} pre {
            background: ${codeBg};
            padding: 12px 14px;
            border-radius: 8px;
            overflow-x: auto;
            margin: 10px 0;
          }
          .${scopeClass} pre code { background: transparent; padding: 0; }
          .${scopeClass} hr {
            border: none;
            border-top: 1px solid ${ruleColor};
            margin: 14px 0;
          }
          .${scopeClass} table {
            border-collapse: collapse;
            width: 100%;
            margin: 10px 0;
            font-size: 14px;
          }
          .${scopeClass} th,
          .${scopeClass} td {
            border: 1px solid ${ruleColor};
            padding: 6px 10px;
            text-align: left;
          }
          .${scopeClass} th { color: ${headerColor}; font-weight: 700; background: ${chipBg}; }
          .${scopeClass} img { max-width: 100%; height: auto; border-radius: 8px; }
        `}</style>

        <div
          className={scopeClass}
          dangerouslySetInnerHTML={{ __html: safeHtml }}
        />
      </div>
    </div>
  );
};

export default InsightPanel;
